/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useMemo } from "react";
import { Tooltip } from "@mui/material";
import {
  StyledSectionHeader,
  StyledSectionActions,
  StyledCounter,
  StyledLinkButton,
  StyledGroupGrid,
  StyledGroupCard,
  StyledGroupHeader,
  StyledGroupLabel,
  StyledModuleRow,
  StyledCheckbox,
  StyledModuleLabel,
  StyledBlockedTag,
  StyledDivergenceTag,
  StyledHelperMessage,
} from "./styles";

const TOP_LEVEL_ONLY_HINT =
  'This module is only reachable by Super Admin / Super Viewer users, so it cannot be granted here. Those users already have access to everything.';

const ADDED_MODULE_HINT = 'Added on top of the selected profile.';
const REMOVED_MODULE_HINT = 'Part of the selected profile, but switched off for this user.';

/**
 * Grouped tick-box grid for the Admin App modules a user may open.
 *
 * Purely presentational: it owns no selection state, and every handler emits the complete next
 * list so the caller stays the single source of truth. Keys the catalogue no longer offers are
 * never emitted, because the server rejects a payload containing one — see `usableSelection`.
 *
 * @param {Array}  groups             catalogue groups, each `{ key, label, modules: [...] }`
 * @param {Array}  selectedKeys       module keys currently granted
 * @param {Set}    moduleKeySet       every key the catalogue offers, for O(1) membership tests
 * @param {Set}    selectableKeySet   the subset that may be granted here — excludes `top_level_only`
 * @param {Object} unavailableModules `{ key: { label, hint } }` for modules this particular user
 *                                    cannot be granted, each with the tag and tooltip explaining
 *                                    why. Subtracted from `selectableKeySet` here as well, so a
 *                                    caller cannot label a row it forgot to exclude.
 * @param {Function} onChange         called with the next full list of keys
 * @param {String} emptySelectionMessage wording of the "nothing selected" notice, so a caller
 *                                    granting something other than a user reads correctly
 * @param {String} summaryLabel       replaces the plain "N of M selected" counter, so a caller
 *                                    holding profile context can name it in the same line
 * @param {Set}    profileKeySet      the selected profile's grantable modules, which turns on the
 *                                    per-module divergence markers. Omit (or pass null) when there
 *                                    is no profile to compare against — every row then reads plain.
 */
export const ModuleAccessSelector = ({
  groups = [],
  selectedKeys = [],
  moduleKeySet,
  selectableKeySet,
  unavailableModules,
  summaryLabel = '',
  profileKeySet = null,
  onChange,
  isLoading = false,
  isError = false,
  onRetry,
  disabled = false,
  testIdPrefix = 'module-access',
  emptySelectionMessage = 'No modules selected - this user can sign in but will not be able to open any part of the Admin App.',
}) => {
  const knownKeys = useMemo(
    () => (moduleKeySet instanceof Set ? moduleKeySet : new Set()),
    [moduleKeySet]
  );

  const profileKeys = useMemo(
    () => (profileKeySet instanceof Set ? profileKeySet : null),
    [profileKeySet]
  );

  const blockedModules = useMemo(
    () => (unavailableModules && typeof unavailableModules === 'object' ? unavailableModules : {}),
    [unavailableModules]
  );

  const selectableKeys = useMemo(() => {
    const offered = selectableKeySet instanceof Set ? selectableKeySet : new Set();
    const blockedKeys = Object.keys(blockedModules);

    if (blockedKeys.length === 0) return offered;

    const grantable = new Set(offered);
    blockedKeys.forEach((key) => grantable.delete(key));

    return grantable;
  }, [selectableKeySet, blockedModules]);

  // A Set, not `includes`: a group's Select all state is recomputed for every module on every
  // render, so linear lookups would make the grid quadratic in the size of the grant.
  const selectedSet = useMemo(
    () => new Set(Array.isArray(selectedKeys) ? selectedKeys : []),
    [selectedKeys]
  );

  // One pass splitting the selection three ways, because each part drives something different:
  // only `usableSelection` may ever be submitted, and the two counts each get their own notice
  // since the reasons are not interchangeable. `staleCount` is a module retired from the registry
  // after this user was granted it; `inapplicableCount` is one the registry still defines but this
  // user cannot be granted — a network-wide-only module, or one their instance scope cannot reach.
  const { usableSelection, staleCount, inapplicableCount } = useMemo(() => {
    const usable = [];
    let stale = 0;
    let inapplicable = 0;

    selectedSet.forEach((key) => {
      if (selectableKeys.has(key)) usable.push(key);
      else if (knownKeys.has(key)) inapplicable += 1;
      else stale += 1;
    });

    return { usableSelection: usable, staleCount: stale, inapplicableCount: inapplicable };
  }, [selectedSet, knownKeys, selectableKeys]);

  const emit = useCallback(
    (nextKeys) => {
      if (disabled || typeof onChange !== 'function') return;
      onChange(nextKeys);
    },
    [disabled, onChange]
  );

  const toggleModule = useCallback(
    (moduleKey) => {
      if (!selectableKeys.has(moduleKey)) return;

      if (selectedSet.has(moduleKey)) {
        emit(usableSelection.filter((key) => key !== moduleKey));
        return;
      }
      emit([...usableSelection, moduleKey]);
    },
    [emit, selectedSet, usableSelection, selectableKeys]
  );

  const toggleGroup = useCallback(
    (groupModules, everySelected) => {
      // Select all skips what cannot be granted here, so it can never produce a selection the
      // admin could not have built by hand.
      const groupKeys = groupModules
        .filter((module) => selectableKeys.has(module?.key))
        .map((module) => module.key);

      if (everySelected) {
        const removed = new Set(groupKeys);
        emit(usableSelection.filter((key) => !removed.has(key)));
        return;
      }

      emit(Array.from(new Set([...usableSelection, ...groupKeys])));
    },
    [emit, usableSelection, selectableKeys]
  );

  const clearAll = useCallback(() => emit([]), [emit]);

  if (isLoading) {
    return <StyledHelperMessage data-testid={`${testIdPrefix}-loading`}>Loading modules…</StyledHelperMessage>;
  }

  if (isError) {
    return (
      <StyledHelperMessage tone="warning" data-testid={`${testIdPrefix}-error`}>
        Could not load the module list.
        {typeof onRetry === 'function' && (
          <StyledLinkButton onClick={onRetry} data-testid={`${testIdPrefix}-retry`}>
            Retry
          </StyledLinkButton>
        )}
      </StyledHelperMessage>
    );
  }

  if (groups.length === 0) {
    return (
      <StyledHelperMessage data-testid={`${testIdPrefix}-empty`}>
        No modules are available to assign.
      </StyledHelperMessage>
    );
  }

  return (
    <div data-testid={testIdPrefix}>
      <StyledSectionHeader>
        <StyledCounter data-testid={`${testIdPrefix}-counter`}>
          {summaryLabel || `${usableSelection.length} of ${selectableKeys.size} selected`}
        </StyledCounter>
        <StyledSectionActions>
          <StyledLinkButton
            onClick={clearAll}
            disabled={disabled || selectedSet.size === 0}
            data-testid={`${testIdPrefix}-clear`}
          >
            Clear
          </StyledLinkButton>
        </StyledSectionActions>
      </StyledSectionHeader>

      <StyledGroupGrid>
        {groups.map((group) => {
          const groupModules = Array.isArray(group?.modules) ? group.modules : [];
          // Both counts ignore modules that cannot be granted here, otherwise a group holding one
          // could never read as fully selected and its Select all would look broken.
          const selectableInGroup = groupModules.filter((module) => selectableKeys.has(module?.key));
          const selectedInGroup = selectableInGroup.filter((module) => selectedSet.has(module.key)).length;
          const everySelected =
            selectableInGroup.length > 0 && selectedInGroup === selectableInGroup.length;

          return (
            <StyledGroupCard key={group?.key} data-testid={`${testIdPrefix}-group-${group?.key}`}>
              <StyledGroupHeader>
                <StyledGroupLabel>{group?.label}</StyledGroupLabel>
                <StyledLinkButton
                  onClick={() => toggleGroup(groupModules, everySelected)}
                  disabled={disabled || selectableInGroup.length === 0}
                  data-testid={`${testIdPrefix}-group-toggle-${group?.key}`}
                >
                  {everySelected ? 'Clear' : 'Select all'}
                </StyledLinkButton>
              </StyledGroupHeader>

              {groupModules.map((module) => {
                const moduleDisabled = disabled || !selectableKeys.has(module?.key);
                // Why the row cannot be ticked, when the label alone does not say. The registry
                // flag and the caller's own rules are separate reasons and read differently, so
                // each supplies its own wording rather than sharing one vague line.
                const blocked = module?.top_level_only
                  ? { label: 'Top-level only', hint: TOP_LEVEL_ONLY_HINT }
                  : blockedModules[module?.key];

                const isSelected = selectedSet.has(module?.key);
                const comparable = Boolean(profileKeys) && selectableKeys.has(module?.key);
                const inProfile = comparable && profileKeys.has(module?.key);
                const addedOutsideProfile = comparable && isSelected && !inProfile;
                const droppedFromProfile = comparable && !isSelected && inProfile;

                return (
                <StyledModuleRow key={module?.key} disabled={moduleDisabled}>
                  <StyledCheckbox
                    size="small"
                    disableRipple
                    checked={isSelected}
                    disabled={moduleDisabled}
                    onChange={() => toggleModule(module?.key)}
                    inputProps={{
                      'aria-label': module?.label,
                      'data-testid': `${testIdPrefix}-checkbox-${module?.key}`,
                    }}
                  />
                  <StyledModuleLabel title={module?.label} struck={droppedFromProfile}>
                    {module?.label}
                  </StyledModuleLabel>
                  {addedOutsideProfile && (
                    <Tooltip title={ADDED_MODULE_HINT} arrow placement="top">
                      <StyledDivergenceTag
                        component="span"
                        tone="added"
                        label="+"
                        size="small"
                        aria-label={ADDED_MODULE_HINT}
                        data-testid={`${testIdPrefix}-added-tag-${module?.key}`}
                      />
                    </Tooltip>
                  )}
                  {droppedFromProfile && (
                    <Tooltip title={REMOVED_MODULE_HINT} arrow placement="top">
                      <StyledDivergenceTag
                        component="span"
                        tone="removed"
                        label="−"
                        size="small"
                        aria-label={REMOVED_MODULE_HINT}
                        data-testid={`${testIdPrefix}-removed-tag-${module?.key}`}
                      />
                    </Tooltip>
                  )}
                  {blocked && (
                    <Tooltip title={blocked.hint} arrow placement="top">
                      <StyledBlockedTag
                        label={blocked.label}
                        size="small"
                        aria-label={blocked.hint}
                        onClick={(event) => event.preventDefault()}
                        data-testid={`${testIdPrefix}-blocked-tag-${module?.key}`}
                      />
                    </Tooltip>
                  )}
                </StyledModuleRow>
                );
              })}
            </StyledGroupCard>
          );
        })}
      </StyledGroupGrid>

      {usableSelection.length === 0 && (
        <StyledHelperMessage tone="warning" data-testid={`${testIdPrefix}-empty-selection-notice`}>
          {emptySelectionMessage}
        </StyledHelperMessage>
      )}

      {inapplicableCount > 0 && (
        <StyledHelperMessage tone="warning" data-testid={`${testIdPrefix}-inapplicable-notice`}>
          {`${inapplicableCount} assigned ${inapplicableCount === 1 ? 'module is' : 'modules are'} not available for this user's access and will be dropped if you change this selection.`}
        </StyledHelperMessage>
      )}

      {staleCount > 0 && (
        <StyledHelperMessage tone="warning" data-testid={`${testIdPrefix}-stale-notice`}>
          {`${staleCount} previously assigned ${staleCount === 1 ? 'module is' : 'modules are'} no longer available and will be dropped if you change this selection.`}
        </StyledHelperMessage>
      )}
    </div>
  );
};

export default ModuleAccessSelector;
